import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { cn, formatNumber } from '@anytag/shared-utils';
import { Payment, PaymentRequestStatus } from '@anytag/graphql';
import { TableCell } from '@anytag/shared-atoms';
import { Badge, BadgeProps } from '@anyui/core';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { useInfluencerPermissions, usePaymentPermissions, useUserRoles } from '@anytag/auth';
import { checkIconImage, closeIconImage, checkIconGrayImage, closeIconGrayImage } from '@anytag/shared-assets';

interface Props {
  onSelectedPayment: (payment: { id: number; name: string; status: PaymentRequestStatus }) => void;
}

export const usePaymentsTable = ({ onSelectedPayment }: Props) => {
  const { t } = useTranslation();
  const { isAdmin } = useUserRoles();
  const { hideSelectInfluencers } = useInfluencerPermissions();
  const { hideFailedBtn, hidePaidBtn } = usePaymentPermissions();
  const { generateProfileLink } = useGenerateInfluencerProfileLink({ disabled: hideSelectInfluencers });

  const getStatusColumnProps = (status: PaymentRequestStatus): BadgeProps => {
    switch (status) {
      case PaymentRequestStatus.PAID:
        return { children: t('Option.Paid'), color: 'zinc' };
      case PaymentRequestStatus.FAILED:
        return { children: t('Option.Failed'), color: 'red' };
      case PaymentRequestStatus.REJECTED:
        return { children: t('Option.Rejected'), color: 'yellow' };
      case PaymentRequestStatus.REVIEWING:
        return { children: t('Option.Reviewing'), color: 'purple' };
      default:
        return { children: null, color: 'zinc' };
    }
  };

  const hasPaidFailed = isAdmin && !hideFailedBtn && !hidePaidBtn;

  const columns: ColumnDef<Payment>[] = [
    {
      accessorKey: 'influencer.name',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => (
        <TableCell.Link label={row.original.influencer.name} {...generateProfileLink(row.original.influencer.id)} />
      )
    },
    {
      accessorKey: 'influencer.email',
      header: t<string>('HeaderColumn.Email Address'),
      cell: ({ row }) => <TableCell.Text value={row.original.influencer.email} className="min-w-25" />
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <Badge {...getStatusColumnProps(row.original.status)} />
    },
    {
      accessorKey: 'grossRevenue',
      header: t<string>('HeaderColumn.Revenue'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.grossRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'fee',
      header: t<string>('HeaderColumn.Fee'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.fee)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'netRevenue',
      header: t<string>('HeaderColumn.Amount'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.netRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'requestedDate',
      header: t<string>('HeaderColumn.Finalized Date'),
      cell: ({ row }) => <TableCell.Date value={row.original.requestedDate} />
    },
    {
      accessorKey: 'dueDate',
      header: t<string>('HeaderColumn.Due Date'),
      cell: ({ row }) => <TableCell.Date value={row.original.dueDate} />
    },
    {
      accessorKey: 'paidDate',
      header: t<string>('HeaderColumn.Paid Date'),
      cell: ({ row }) => <TableCell.Date value={row.original.paidDate} />
    },
    {
      accessorKey: 'paid',
      header: t<string>('HeaderColumn.Paid'),
      meta: { align: 'right', hidden: !hasPaidFailed },
      cell: ({ row }) => {
        const disabledPaidFailedColumn = row.original.status !== 'REVIEWING';

        return (
          <img
            src={disabledPaidFailedColumn ? checkIconGrayImage : checkIconImage}
            className={cn(styles.icon, { 'pointer-events-none bg-dark-gray': disabledPaidFailedColumn })}
            onClick={() =>
              onSelectedPayment({
                id: row.original.id,
                name: row.original.influencer.name,
                status: PaymentRequestStatus.PAID
              })
            }
            alt="Paid"
          />
        );
      }
    },
    {
      accessorKey: 'failed',
      header: t<string>('HeaderColumn.Failed'),
      meta: { align: 'right', hidden: !hasPaidFailed },
      cell: ({ row }) => {
        const disabledPaidFailedColumn = row.original.status !== 'REVIEWING';

        return (
          <img
            src={disabledPaidFailedColumn ? closeIconGrayImage : closeIconImage}
            className={cn(styles.icon, { 'pointer-events-none bg-dark-gray': disabledPaidFailedColumn })}
            onClick={() =>
              onSelectedPayment({
                id: row.original.id,
                name: row.original.influencer.name,
                status: PaymentRequestStatus.FAILED
              })
            }
            alt="Failed"
          />
        );
      }
    }
  ];

  return { columns };
};
const styles = {
  icon: cn('mx-auto my-0 w-5.5 cursor-pointer rounded-sm border border-border bg-white p-1')
};
